import { useMemo } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Target } from 'lucide-react';
import { BRAND } from '@/lib/brand';
import { cn } from '@/lib/utils';
import { evaluateMetric } from '@/features/intelligence/dashboard/evaluate';
import { formatUnit } from '@/features/intelligence/dashboard/series';
import type { MetricWidget } from '@/features/intelligence/dashboard/types';
import { useDashboard, useSourceState } from '../DashboardContext';
import { ChartTooltipContent, DeltaBadge, WidgetError, WidgetMessage, WidgetSkeleton } from '../shared';

export function MetricWidgetView({ widget }: { widget: MetricWidget }) {
  const { bag, catalog, window } = useDashboard();
  const { loading, error, missing } = useSourceState([widget.metric]);
  const def = catalog.metric(widget.metric);

  const result = useMemo(() => (def ? evaluateMetric(def, bag, window, catalog) : null), [def, bag, window, catalog]);

  if (missing.length > 0 || !def || !result) {
    return <WidgetMessage title="Metric unavailable" detail="This metric was removed or isn't available to your role." />;
  }
  if (error) return <WidgetError message={error} />;
  if (loading || !result.ready) return <WidgetSkeleton variant="metric" />;
  if (result.unavailable) return <WidgetMessage title="Not available yet" detail={result.unavailable} />;

  const showSpark = widget.showSparkline && widget.h >= 2 && result.sparkline.length > 1 && result.sparkline.some((p) => p.v !== 0);
  const value = result.value;
  const target = widget.target;
  const progress = target != null && target !== 0 && value != null ? value / target : null;
  const deltaLabel = def.grain === 'snapshot' ? 'current' : window.comparisonLabel;
  const unitHint = def.grain === 'month' ? 'monthly totals' : def.grain === 'snapshot' ? 'right now' : null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-baseline justify-between gap-2">
        <span
          className={cn(
            'font-medium tracking-headline text-foreground tabular-nums',
            widget.h >= 2 ? 'text-2xl' : 'text-xl',
          )}
        >
          {formatUnit(value, def.unit, { compact: widget.w <= 2 })}
        </span>
        {unitHint && widget.w >= 3 && <span className="text-[11px] text-muted-foreground">{unitHint}</span>}
      </div>
      <div className="mt-0.5 flex min-h-[16px] flex-wrap items-center gap-x-3 gap-y-0.5">
        {widget.showDelta && def.grain !== 'snapshot' && (
          <DeltaBadge delta={result.delta} direction={def.direction} label={widget.w >= 3 ? deltaLabel : undefined} />
        )}
        {target != null && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
            <Target className="h-3 w-3" strokeWidth={2} />
            {formatUnit(target, def.unit, { compact: true })}
            {progress != null && <span className="font-medium text-foreground">{Math.round(progress * 100)}%</span>}
          </span>
        )}
      </div>
      {target != null && progress != null && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
          <div
            className={cn(
              'h-full rounded-full',
              (def.direction === 'down' ? progress <= 1 : progress >= 1) ? 'bg-bright-moss-dark' : 'bg-atmosphere-dark',
            )}
            style={{ width: `${Math.min(100, Math.max(2, progress * 100))}%` }}
          />
        </div>
      )}
      {showSpark && (
        <div className="mt-auto min-h-0 flex-1 pt-2">
          <div className="h-full min-h-[36px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={result.sparkline} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`spark-${widget.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BRAND.oldGrowth} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={BRAND.oldGrowth} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <Tooltip
                  cursor={{ stroke: BRAND.linenDark }}
                  content={
                    <ChartTooltipContent
                      grain={result.grain}
                      infoByKey={{ v: { label: def.label, unit: def.unit, color: BRAND.oldGrowth } }}
                    />
                  }
                />
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke={BRAND.oldGrowth}
                  strokeWidth={1.5}
                  fill={`url(#spark-${widget.id})`}
                  isAnimationActive={false}
                  connectNulls
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
